<template>
  <div class="px-[24px] pt-[max(32px,env(safe-area-inset-top))] pb-[32px] font-[Inter]">
    <h4 class="text-text-display-lg font-black tracking-[-0.03em] mb-[8px] text-brown-800">
      Almost there
    </h4>
    <div class="font-semibold mb-[24px] text-brown-800">
      Enjoy a personalised app experience when you share more about yourself
    </div>

    <div class="w-full mb-[24px] sm:mb-[48px] flex flex-col gap-6">
      <div>
        <label :class="labelClass" for="stageOfLife">Stage of life</label>
        <DsDropdown
          id="stageOfLife"
          name="stageOfLife"
          :model-value="data.stageOfLife"
          :options="stageOfLifeOptions"
          placeholder="Select an option"
          @update:modelValue="onStageOfLifeChange"
        />
      </div>

      <div v-if="data.stageOfLife === 'OTHERS'">
        <DsTextarea
          id="otherStageOfLife"
          name="otherStageOfLife"
          v-model="data.otherStageOfLife"
          placeholder="Please specify your stage of life"
          :error="otherStageOfLifeError"
        />
      </div>

      <template v-if="data.stageOfLife === 'STUDYING'">
        <div>
          <label :class="labelClass" for="levelId">Level of Study</label>
          <DsDropdown
            id="levelId"
            name="levelId"
            :model-value="data.levelId"
            :options="levelOfStudyOptions"
            placeholder="Select an option"
            @update:modelValue="onLevelChange"
          />
        </div>

        <div>
          <label :class="labelClass" for="schoolId">School</label>
          <DsSearchableDropdown
            id="schoolId"
            name="schoolId"
            :model-value="data.schoolId"
            :options="schoolOptions"
            placeholder="Select an option"
            search-placeholder="Search school"
            @update:modelValue="onSchoolChange"
          />
        </div>

        <div v-if="data.schoolId === OTHER_SCHOOL_ID">
          <DsTextarea
            id="otherSchool"
            name="otherSchool"
            v-model="data.otherSchool"
            placeholder="Enter school name here"
            :max-length="OTHER_SCHOOL_MAX_LENGTH"
            :error="otherSchoolError"
          />
        </div>
      </template>

      <CareerFields
        v-if="careerFieldsEnabled"
        v-model="careerFields"
        :stage-of-life="data.stageOfLife"
        :level-id="data.levelId"
      />

      <div>
        <label :class="labelClass" for="maritalStatus">Marital Status</label>
        <DsDropdown
          id="maritalStatus"
          name="maritalStatus"
          :model-value="data.maritalStatus"
          :options="maritalStatuses"
          placeholder="Select an option"
          @update:modelValue="value => setField('maritalStatus', value)"
        />
      </div>

      <div>
        <span :class="labelClass">Do you have kids?</span>
        <div class="flex gap-2">
          <DsRadioCard
            v-for="option in haveChildrenOptions"
            :key="String(option.value)"
            class="flex-1"
            name="haveChildren"
            align="center"
            hide-indicator
            :label="option.label"
            :value="option.value"
            :model-value="data.haveChildren"
            @update:modelValue="onHaveChildrenChange"
          />
        </div>
      </div>

      <div v-if="data.haveChildren === true">
        <label :class="labelClass" for="numberOfChildren">How many kids do you have?</label>
        <DsDropdown
          id="numberOfChildren"
          name="numberOfChildren"
          :model-value="data.numberOfChildren"
          :options="childrenNumberOptions"
          placeholder="Select an option"
          @update:modelValue="value => setField('numberOfChildren', value)"
        />
      </div>
    </div>
  </div>

  <div class="px-[24px] pb-[max(32px,env(safe-area-inset-bottom))] font-[Inter]">
    <div class="flex flex-row justify-center text-center mb-[10px] text-brown-800 text-text-body">
      <div class="font-bold">Note:</div>
      <div class="ml-[4px]">You can change your details anytime under "My Profile."</div>
    </div>
    <div
      class="flex justify-center"
      :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''"
    >
      <DsButton
        variant="secondary"
        shape="pill"
        size="56"
        class="w-full !text-text-body-md"
        :disabled="isButtonDisabled"
        :loading="isSaving"
        @click="handleSave"
      >
        {{ isSaving ? 'Saving...' : 'Save and Next' }}
      </DsButton>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import {
  Button as DsButton,
  Dropdown as DsDropdown,
  RadioCard as DsRadioCard,
  SearchableDropdown as DsSearchableDropdown,
  Textarea as DsTextarea,
} from '@team-hogc/hogc-one-design-system';
import { stageOfLife } from '@/constants/profile/stageOfLife';
import { maritalStatuses } from '@/constants/register/maritalStatuses';
import { childrenNumber } from '@/constants/register/childrenNumber';
import { REGEX_SCHOOL_NAME, REGEX_STAGE_OF_LIFE } from '@/constants/validation/regex-patterns';
import {
  ERROR_VALID_SCHOOL_NAME,
  ERROR_VALID_STAGE_OF_LIFE,
} from '@/constants/validation/error-messages';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';
import CareerFields from '@/components/common/CareerFields.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  CAREER_FIELD_KEYS,
  PROFILE_CAREER_FIELDS_FLAG,
  careerFieldsPayload,
} from '@/constants/profile/careerFields';

// "Others" in the school list. A real id, so it has to survive as 0 rather
// than being treated as "nothing selected".
const OTHER_SCHOOL_ID = '0';
const OTHER_SCHOOL_MAX_LENGTH = 40;

// The "Others" text for stage-3, or nothing. The field holds '' for every other stage of
// life, and the API validates an optional field whenever it is present, so "" is a 400.
const otherPhaseOf = form =>
  form.stageOfLife === 'OTHERS' ? form.otherStageOfLife?.trim() || undefined : undefined;

export default {
  mixins: [keyboardFieldIntoView],
  components: {
    CareerFields,
    DsButton,
    DsDropdown,
    DsRadioCard,
    DsSearchableDropdown,
    DsTextarea,
  },
  data() {
    return {
      agree: false,
      // The dropdowns work in strings (the design system matches option values
      // by identity); ids go back to numbers in the payload.
      data: {
        stageOfLife: '',
        otherStageOfLife: '',
        levelId: '',
        schoolId: '',
        otherSchool: '',
        maritalStatus: '',
        haveChildren: null,
        numberOfChildren: '',
      },
      maritalStatuses,
      isSaving: false,
      careerFields: Object.fromEntries(CAREER_FIELD_KEYS.map(key => [key, null])),
      labelClass: 'block text-base text-primary font-inter font-medium mb-1 leading-5',
      haveChildrenOptions: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
      OTHER_SCHOOL_ID,
      OTHER_SCHOOL_MAX_LENGTH,
    };
  },
  computed: {
    ...mapState('profile', {
      formloading: state => state.loading,
      register: state => state.register,
      errorMessages: state => state.errorMessages,
      schools: state => state.schools,
    }),
    ...mapState('user', ['token']),
    careerFieldsEnabled() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(PROFILE_CAREER_FIELDS_FLAG);
    },
    stageOfLifeOptions() {
      return stageOfLife.map(({ value, label }) => ({ value, label }));
    },
    levelOfStudyOptions() {
      const levels = this.$store.getters['profile/levelOfStudy'];
      if (!levels) return [];
      return levels.map(({ value, label }) => ({ value: String(value), label }));
    },
    childrenNumberOptions() {
      return childrenNumber.map(({ value, label }) => ({ value: String(value), label }));
    },
    schoolOptions() {
      const levelId = this.data.levelId;
      const others = { value: OTHER_SCHOOL_ID, label: 'Others' };
      if (!levelId) return [others];

      const schools = this.$store.getters['profile/schoolsByLevel'](Number(levelId));
      if (!schools || schools.length === 0) return [others];

      return [...schools.map(({ value, label }) => ({ value: String(value), label })), others];
    },
    otherStageOfLifeError() {
      if (this.data.stageOfLife !== 'OTHERS') return '';
      const value = (this.data.otherStageOfLife || '').trim();
      if (!value) return '';
      return REGEX_STAGE_OF_LIFE.test(value) ? '' : ERROR_VALID_STAGE_OF_LIFE;
    },
    otherSchoolError() {
      const value = (this.data.otherSchool || '').trim();
      if (!value) return '';
      return REGEX_SCHOOL_NAME.test(value) ? '' : ERROR_VALID_SCHOOL_NAME;
    },
    isButtonDisabled() {
      const form = this.data;

      // Always required fields
      if (!form.stageOfLife || !form.maritalStatus || form.haveChildren === null) {
        return true;
      }

      // If "Others" is selected, otherStageOfLife is required and must be valid
      if (form.stageOfLife === 'OTHERS') {
        if (!form.otherStageOfLife?.trim() || this.otherStageOfLifeError) return true;
      }

      // Education fields - required if studying
      if (form.stageOfLife === 'STUDYING') {
        if (!form.levelId || form.schoolId === '' || form.schoolId === null) return true;
        // If "Others" is selected for school, otherSchool is required
        if (form.schoolId === OTHER_SCHOOL_ID) {
          if (!form.otherSchool?.trim() || this.otherSchoolError) return true;
        }
      }

      // Children fields - required if have children
      if (form.haveChildren === true && !form.numberOfChildren) {
        return true;
      }

      return this.isSaving;
    },
  },
  watch: {
    // The two free-text fields are the only ones not written through
    // `setField`, and the store is what survives a step back and forward.
    'data.otherStageOfLife'(value) {
      if (this.register.otherStageOfLife !== value) {
        this.inputRegisterForm('otherStageOfLife', value);
      }
    },
    'data.otherSchool'(value) {
      if (this.register.otherSchool !== value) {
        this.inputRegisterForm('otherSchool', value);
      }
    },
  },
  async mounted() {
    this.startKeyboardOverlapTracking();

    await this.$store.dispatch('profile/getSchools');
    await this.$store.dispatch('profile/fetchProfile');
    // Optional and not part of isButtonDisabled: a member may skip all three.
    this.careerFields = Object.fromEntries(
      CAREER_FIELD_KEYS.map(key => [key, this.register[key] ?? null]),
    );
    this.initializeFormFields();
  },
  methods: {
    initializeFormFields() {
      // A custom school name means "Others" was chosen: the backend keeps the
      // previous schoolId on the member row and stores the typed name on the
      // lifePhase row, so the name is what tells the two apart.
      const storedSchoolName = this.$store.state.profile?.schoolName || this.register.otherSchool;
      const schoolId = storedSchoolName
        ? OTHER_SCHOOL_ID
        : this.toOptionValue(this.register.schoolId);

      Object.assign(this.data, {
        stageOfLife: this.register.stageOfLife || '',
        otherStageOfLife:
          this.register.otherStageOfLife ??
          this.register.otherPhaseName ??
          this.$store.state.profile?.otherPhaseName ??
          '',
        levelId: this.toOptionValue(this.register.levelId),
        schoolId,
        otherSchool: storedSchoolName || '',
        maritalStatus: this.register.maritalStatus || '',
        haveChildren: this.register.haveChildren ?? null,
        numberOfChildren:
          this.register.haveChildren === true
            ? this.toOptionValue(this.register.numberOfChildren)
            : '',
      });
    },
    toOptionValue(value) {
      return value === null || value === undefined || value === '' ? '' : String(value);
    },
    setField(field, value) {
      this.data[field] = value;
      this.inputRegisterForm(field, this.toStoreValue(field, value));
    },
    toStoreValue(field, value) {
      const numericFields = ['levelId', 'schoolId', 'numberOfChildren'];
      if (numericFields.includes(field)) {
        return value === '' || value === null ? null : Number(value);
      }
      return value;
    },
    onStageOfLifeChange(value) {
      this.setField('stageOfLife', value);
      // Leaving the conditional answers behind would save a school for a member
      // who no longer says they are studying.
      if (value !== 'OTHERS') this.data.otherStageOfLife = '';
      if (value !== 'STUDYING') {
        this.data.levelId = '';
        this.data.schoolId = '';
        this.data.otherSchool = '';
      }
    },
    onLevelChange(value) {
      this.setField('levelId', value);
      // The school list is per level, so a school chosen under the old level
      // is not an option any more.
      this.data.schoolId = '';
      this.data.otherSchool = '';
    },
    onSchoolChange(value) {
      this.setField('schoolId', value);
      if (value !== OTHER_SCHOOL_ID) this.data.otherSchool = '';
    },
    onHaveChildrenChange(value) {
      this.setField('haveChildren', value);
      if (value !== true) this.data.numberOfChildren = '';
    },
    inputRegisterForm(key, value) {
      this.$store.dispatch('profile/inputRegisterForm', { key, value });
    },
    async handleSave() {
      if (this.isSaving) return;

      this.isSaving = true;

      try {
        const form = this.data;
        const otherSchool = form.schoolId === OTHER_SCHOOL_ID ? form.otherSchool : '';

        // Extract ID from selected level of study (levelId is now the numeric ID)
        const levelRankId = form.levelId ? Number(form.levelId) : undefined;

        // Extract ID from selected stage of life
        const phaseId = stageOfLife.find(stage => stage.value === form.stageOfLife)?.id;

        // Extract school name based on id
        let school = undefined;
        if (form.schoolId && form.schoolId !== OTHER_SCHOOL_ID && form.levelId) {
          const schools = this.$store.getters['profile/schoolsByLevel'](Number(form.levelId));
          if (schools && schools.length > 0) {
            const selectedSchool = schools.find(s => String(s.value) === form.schoolId);
            school = selectedSchool ? selectedSchool.label : undefined;
          }
        }

        const schoolId =
          form.schoolId && form.schoolId !== OTHER_SCHOOL_ID ? Number(form.schoolId) : undefined;
        const numberOfChildren =
          form.haveChildren === true && form.numberOfChildren
            ? Number(form.numberOfChildren)
            : undefined;

        await axios.request(process.env.VUE_APP_API_URL + `/v2/profile/stage-3`, {
          method: 'put',
          data: {
            stageOfLife: form.stageOfLife,
            maritalStatus: form.maritalStatus,
            haveChildren: form.haveChildren,
            numberOfChildren,
            phaseId,
            levelRankId,
            schoolId,
            schoolName: otherSchool || school,
            otherPhase: otherPhaseOf(form),
            ...(this.careerFieldsEnabled && careerFieldsPayload(this.careerFields)),
          },
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        // If "Others" was selected, explicitly maintain schoolId: 0 in the register state
        // This prevents the form from resetting when profile data is re-fetched
        if (form.schoolId === OTHER_SCHOOL_ID && otherSchool?.trim()) {
          this.inputRegisterForm('schoolId', 0);
          this.inputRegisterForm('otherSchool', otherSchool.trim());
        }

        this.inputRegisterForm('stage', 6);
        // Save onboarding status to localStorage after successful stage-3 completion
        this.$store.dispatch('profile/updateOnboardingStatusAfterStage', 3);
      } catch (error) {
        console.error('Stage 3 save error:', error);
        this.$store.dispatch('toast/error', 'Something went wrong saving your details');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
